/**
 * NAS 보관 API 클라이언트 — backend /nas/* 와 1:1 (v2.95.0).
 *
 * v2.114.0 에서 파일탐색기(개인 계정 · /account·/browse·/file·/upload·…)가 아래쪽에 붙었다.
 * 두 묶음은 권한이 다르다 — 위쪽 문서 보관은 sales·admin, 아래쪽 탐색기는 로그인한 전 직원이
 * **본인 NAS 계정**으로 쓴다(무엇이 보이나는 NAS 가 정한다). 섞어 쓰지 말 것.
 */

import type { ApiResponse } from './types';

const BASE = '/api';

async function unwrap<T>(r: Response): Promise<T> {
  const json = (await r.json()) as ApiResponse<T>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

/** 화면이 보낼 수 있는 문서 종류 — 서버 화이트리스트와 같아야 한다. */
export type NasDocType = 'quote' | 'po' | 'tech-support' | 'attachment';

export interface NasStatus {
  ready: boolean;
  reason: string | null;
  host: string;
  share: string;
}

export interface NasTargetInfo {
  canSave: boolean;
  reason: string | null;
  label: string;
  path: string | null;
  linkHint: { kind: string; id: number } | null;
}

export interface NasSaveResult {
  path: string;
  savedName: string;
  bytes: number;
  /** 같은 이름이 있어 이름을 비켜 저장한 경우 true */
  renamed: boolean;
}

export interface NasLogRow {
  id: string;
  docType: string;
  docId: number;
  label: string;
  path: string | null;
  bytes: number | null;
  status: 'success' | 'failed' | 'blocked';
  message: string | null;
  savedBy: string;
  savedAt: string;
}

export async function fetchNasStatus(): Promise<NasStatus> {
  const r = await fetch(`${BASE}/nas/status`, { credentials: 'include' });
  return unwrap<NasStatus>(r);
}

/** 저장될 위치 미리보기 — 무거운 문서 생성은 하지 않는다(경로만 계산). */
export async function fetchNasTarget(docType: NasDocType, docId: number): Promise<NasTargetInfo> {
  const r = await fetch(
    `${BASE}/nas/target?docType=${encodeURIComponent(docType)}&docId=${docId}`,
    { credentials: 'include' },
  );
  return unwrap<NasTargetInfo>(r);
}

export async function saveToNas(docType: NasDocType, docId: number): Promise<NasSaveResult> {
  const r = await fetch(`${BASE}/nas/save`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ docType, docId }),
  });
  return unwrap<NasSaveResult>(r);
}

export async function listNasLogs(limit = 50): Promise<NasLogRow[]> {
  const r = await fetch(`${BASE}/nas/logs?limit=${limit}`, { credentials: 'include' });
  const data = await unwrap<{ items: NasLogRow[] }>(r);
  return data.items;
}

/* ─────────────────────────────────────────────────────────────
   파일탐색기 · 개인 NAS 계정 (v2.114.0 · 2026-09-02)
   ─────────────────────────────────────────────────────────────
   위쪽 「문서 보관」(/nas/status·/target·/save)과 쓰임이 다르다.
   저쪽은 포털이 만든 문서를 정해진 자리에 자동으로 쌓는 것이고, 여기는 **사람이**
   본인 NAS 계정으로 공유 폴더를 눌러 다니는 것이다. 그래서 상태도 /status 가 아니라
   /account 를 본다 — 「서버가 준비됐나」에 더해 「내 계정이 연결됐나」가 같이 필요하다.
   ───────────────────────────────────────────────────────────── */

/** 내 연결 상태 — 비밀번호는 어떤 형태로도 내려오지 않는다. */
export interface NasAccountStatus {
  connected: boolean;
  nasUsername: string | null;
  status: 'ACTIVE' | 'ERROR' | null;
  lastVerifiedAt: string | null;
  lastError: string | null;
  connectedAt: string | null;
}

/** GET /account — 연결 상태 + **이 서버가 탐색기를 돌릴 수 있나**(둘은 별개다). */
export interface NasAccount extends NasAccountStatus {
  ready: boolean;
  readyReason: string | null;
  host: string;
  share: string;
}

export interface NasEntry {
  name: string;
  isDir: boolean;
  /** 폴더는 0 */
  size: number;
  /** ISO 문자열 또는 NAS 가 준 원문 (해석 실패 시) */
  modified: string;
  /** 속성 그대로 — D=폴더 H=숨김 R=읽기전용 S=시스템 */
  attrs: string;
}

export interface NasListing {
  path: string[];
  /** NAS 창에서 보는 것과 같은 역슬래시 표기 */
  pathText: string;
  /** 서버가 이미 정렬해 준다 (폴더 먼저 → 한글 이름순) */
  entries: NasEntry[];
}

export interface NasUploadResult {
  savedName: string;
  bytes: number;
  /** 같은 이름이 있어 이름을 비켜 저장한 경우 true (덮어쓰기는 서버가 절대 하지 않는다) */
  renamed: boolean;
}

export interface NasAccessLogRow {
  id: string;
  userId: string;
  userName: string;
  nasUser: string | null;
  action: string;
  path: string;
  name: string | null;
  bytes: number | null;
  result: string;
  message: string | null;
  at: string;
}

/**
 * `?path=A&path=B` — 폴더 한 단계당 path 를 하나씩 붙인다.
 * ⚠한 덩어리(`A\B`)로 보내면 서버가 조각마다 하는 안전 검사를 통과해 버린다 — 반드시 나눠 보낼 것.
 * 루트는 아예 안 붙인다(빈 문자열 조각은 서버가 거절한다).
 */
function nasPathQuery(path: string[]): string {
  return path.map((s) => `path=${encodeURIComponent(s)}`).join('&');
}

export async function fetchNasAccount(): Promise<NasAccount> {
  const r = await fetch(`${BASE}/nas/account`, { credentials: 'include' });
  return unwrap<NasAccount>(r);
}

/**
 * 연결 — 서버가 **실제로 NAS 로그인을 해 보고** 성공해야 저장한다(실패 시 아무것도 안 남는다).
 * ⚠응답에는 ready·host·share 가 없다(연결 상태만 돌려준다) — 그 값이 필요하면 GET 을 다시 읽을 것.
 */
export async function connectNasAccount(username: string, password: string): Promise<NasAccountStatus> {
  const r = await fetch(`${BASE}/nas/account`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  return unwrap<NasAccountStatus>(r);
}

export async function disconnectNasAccount(): Promise<{ removed: boolean }> {
  const r = await fetch(`${BASE}/nas/account`, { method: 'DELETE', credentials: 'include' });
  return unwrap<{ removed: boolean }>(r);
}

export async function browseNas(path: string[]): Promise<NasListing> {
  const qs = nasPathQuery(path);
  const r = await fetch(`${BASE}/nas/browse${qs ? `?${qs}` : ''}`, { credentials: 'include' });
  return unwrap<NasListing>(r);
}

/**
 * 내려받기 — **fetch 로 받지 않는다.**
 * 최대 200MB 라 응답을 메모리(blob)에 통째로 담으면 큰 파일에서 탭이 멈춘다.
 * 앵커로 열어 브라우저가 그대로 흘려받게 두는 것이 핵심(파일명은 서버 Content-Disposition).
 */
export function downloadNasFile(path: string[], name: string): void {
  const qs = nasFileQuery(path, name);
  const a = document.createElement('a');
  a.href = `${BASE}/nas/file?${qs}`;
  a.download = name;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/* ── 미리보기·메일 첨부 (v2.313.0 · 개발요청 #26·#27) ───────────────────── */

/**
 * 미리보기 형식 — ⚠서버 backend/src/lib/nas/file-kind.ts 의 NAS_PREVIEW_EXT 와 **같은 목록**(check:nas-preview-mail 이 대조).
 *   office(워드·파워포인트 docx·pptx)는 서버가 PDF 로 바꿔 준다 · 옛 doc·ppt 와 svg 는 일부러 없다.
 */
export const NAS_PREVIEW_EXT = {
  image: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'],
  pdf: ['pdf'],
  sheet: ['xlsx', 'xlsm', 'xls', 'csv'],
  office: ['docx', 'pptx'],
} as const;
export type NasPreviewKind = keyof typeof NAS_PREVIEW_EXT | 'none';
/** 서버 NAS_PREVIEW_MAX_BYTES 와 같은 값 — 넘으면 누르기 전에 안내(서버도 거절한다) */
export const NAS_PREVIEW_MAX_BYTES = 30 * 1024 * 1024;

export function nasPreviewKind(name: string): NasPreviewKind {
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
  if (!ext) return 'none';
  for (const [kind, list] of Object.entries(NAS_PREVIEW_EXT) as Array<[keyof typeof NAS_PREVIEW_EXT, readonly string[]]>) {
    if (list.includes(ext)) return kind;
  }
  return 'none';
}

function nasFileQuery(path: string[], name: string): string {
  return [...path.map((s) => `path=${encodeURIComponent(s)}`), `name=${encodeURIComponent(name)}`].join('&');
}

/**
 * 미리보기 창에 넘길 값 — 열기는 부르는 쪽이 `openPdfPreview` 로(전역 미리보기 창 · lib/pdf-preview.ts).
 *   · url = 서버 /nas/preview (워드·파워포인트는 PDF 로 바뀌어 오므로 형식을 PDF 로 알려 준다)
 *   · downloadUrl = 원본 내려받기(/nas/file) — 창의 [⬇ 저장]은 지금의 [내려받기]와 같다
 *   · 미리보기가 없는 형식(한글·압축 등)도 창을 연다 — 「이 형식은 미리보기를 지원하지 않습니다 + 저장」 안내가 뜬다
 */
export function nasPreviewTarget(path: string[], name: string): { url: string; downloadUrl: string; filename: string; mime?: string; noPreview?: boolean } {
  const qs = nasFileQuery(path, name);
  const kind = nasPreviewKind(name);
  const downloadUrl = `${BASE}/nas/file?${qs}`;
  // 미리보기 없음 — 창이 이름(svg 등)을 보고 그림으로 그리려다 원본을 통째로 받지 않게 못 박는다
  if (kind === 'none') return { url: downloadUrl, downloadUrl, filename: name, noPreview: true };
  return {
    url: `${BASE}/nas/preview?${qs}`,
    downloadUrl,
    filename: name,
    mime: kind === 'office' || kind === 'pdf' ? 'application/pdf' : /\.csv$/i.test(name) ? 'text/csv' : undefined,
  };
}

/** 메일 첨부 좌표 — 내용은 서버가 보낼 때 본인 NAS 계정으로 가져온다(화면은 파일을 올리지 않는다) */
export function nasMailDocRef(path: string[], entry: Pick<NasEntry, 'name' | 'size'>): {
  docType: 'nas-file'; docId: string; params: { path: string[]; name: string }; label: string; sizeHint: number;
} {
  return { docType: 'nas-file', docId: 'nas', params: { path: [...path], name: entry.name }, label: entry.name, sizeHint: entry.size };
}

/** 같은 NAS 파일인가 (메일 창에 두 번 붙지 않게) */
export function nasFileKey(path: readonly string[], name: string): string {
  return JSON.stringify([...path, name]);
}

/** 올리기 — Content-Type 을 직접 지정하지 말 것(브라우저가 multipart 경계를 붙인다). */
export async function uploadToNas(path: string[], file: File): Promise<NasUploadResult> {
  const fd = new FormData();
  for (const seg of path) fd.append('path', seg);
  fd.append('file', file);
  const r = await fetch(`${BASE}/nas/upload`, { method: 'POST', credentials: 'include', body: fd });
  return unwrap<NasUploadResult>(r);
}

export async function createNasFolder(path: string[], name: string): Promise<{ name: string }> {
  const r = await fetch(`${BASE}/nas/folder`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, name }),
  });
  return unwrap<{ name: string }>(r);
}

export async function renameNasEntry(path: string[], name: string, newName: string): Promise<{ name: string }> {
  const r = await fetch(`${BASE}/nas/rename`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, name, newName }),
  });
  return unwrap<{ name: string }>(r);
}

/** 지우기 — 폴더는 비어 있을 때만 지워진다(몇 개 남았는지는 서버 문구가 알려 준다). */
export async function deleteNasEntry(
  path: string[],
  name: string,
): Promise<{ name: string; kind: 'file' | 'dir' }> {
  const r = await fetch(`${BASE}/nas/delete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path, name }),
  });
  return unwrap<{ name: string; kind: 'file' | 'dir' }>(r);
}

/** 접근 기록 (admin 전용) — 「누가 무엇을 지웠나」는 NAS 자체 로그로는 사람을 가를 수 없어 여기 남는다. */
export async function listNasAccessLogs(limit = 100): Promise<NasAccessLogRow[]> {
  const r = await fetch(`${BASE}/nas/access-log?limit=${limit}`, { credentials: 'include' });
  const data = await unwrap<{ items: NasAccessLogRow[] }>(r);
  return data.items;
}
